import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { importarClientesAnsesCsv } from '../../services/clienteService';

// Importación (una sola vez) de clientes desde el CSV "TURNOS ANSES" del estudio. Flujo en dos
// pasos: Simular (no persiste, previsualiza) → Confirmar (crea los clientes nuevos). Dedupe por
// CUIL: los clientes que ya existen no se tocan. Ver ImportacionClientesAnsesService en el backend.
export default function ImportarClientesAnses() {
    const navigate = useNavigate();
    const [archivo, setArchivo] = useState(null);
    const [procesando, setProcesando] = useState(false);
    const [resultado, setResultado] = useState(null);
    const [error, setError] = useState('');
    const [confirmado, setConfirmado] = useState(false);

    const handleArchivo = (e) => {
        setArchivo(e.target.files?.[0] || null);
        setResultado(null);
        setError('');
        setConfirmado(false);
    };

    const ejecutar = async (simulacion) => {
        if (!archivo) {
            setError('Elegí primero el archivo CSV.');
            return;
        }
        setError('');
        setProcesando(true);
        try {
            const data = await importarClientesAnsesCsv(archivo, simulacion);
            setResultado(data);
            if (!simulacion) setConfirmado(true);
        } catch (e) {
            setError(e?.response?.data?.mensaje || 'Error al procesar el archivo.');
        } finally {
            setProcesando(false);
        }
    };

    return (
        <div style={{ padding: '24px', minHeight: '100vh', background: '#f8fafc' }}>
            <div style={{ maxWidth: 1000, margin: '0 auto' }}>
                <button className="btn btn-link px-0 mb-2" onClick={() => navigate('/clientes')}>
                    <i className="bi bi-arrow-left me-1" />Volver a clientes
                </button>
                <h2 style={{ margin: 0, fontWeight: 700, color: '#1e293b', fontSize: '1.5rem' }}>
                    <i className="bi bi-people-fill me-2" style={{ color: '#3b82f6' }} />
                    Importar clientes (CSV TURNOS ANSES)
                </h2>
                <p style={{ margin: '4px 0 20px', color: '#64748b', fontSize: '0.9rem' }}>
                    Da de alta los clientes del archivo (con CUIL, teléfono, claves cifradas y
                    referencia; el trámite queda en la observación). Además crea los <strong>turnos
                    ANSES futuros</strong> con su cita en el turnero (carga silenciosa, sin
                    recordatorios ni trámite asociado); los turnos pasados no se cargan.
                    <strong> Dedupe por CUIL</strong> (clientes) y por cliente+fecha+hora (turnos):
                    lo que ya existe no se duplica. Primero <strong>simulá</strong> y revisá antes de confirmar.
                </p>

                <input
                    type="file"
                    accept=".csv,text/csv"
                    onChange={handleArchivo}
                    className="form-control mb-3"
                    style={{ maxWidth: 480 }}
                />

                <div className="d-flex gap-2 flex-wrap mb-3">
                    <button
                        className="btn btn-outline-primary"
                        onClick={() => ejecutar(true)}
                        disabled={procesando || !archivo}
                        style={{ fontWeight: 600 }}
                    >
                        {procesando
                            ? <><span className="spinner-border spinner-border-sm me-2" />Procesando…</>
                            : <><i className="bi bi-search me-2" />Simular</>}
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={() => ejecutar(false)}
                        disabled={procesando || !archivo || !resultado || confirmado}
                        style={{ fontWeight: 600 }}
                        title={!resultado ? 'Primero simulá para revisar' : ''}
                    >
                        <i className="bi bi-check-lg me-2" />Confirmar importación
                    </button>
                </div>

                {error && <div className="alert alert-danger py-2">{error}</div>}

                {resultado && (
                    <div>
                        <div
                            className={`alert ${resultado.simulacion ? 'alert-info' : 'alert-success'} py-2`}
                            style={{ fontSize: '0.9rem' }}
                        >
                            <strong>
                                {resultado.simulacion ? 'Simulación (no se guardó nada).' : '¡Importación realizada!'}
                            </strong>{' '}
                            {resultado.simulacion ? 'Se crearían' : 'Se crearon'}{' '}
                            <strong>{resultado.nuevos}</strong> clientes nuevos ·{' '}
                            <strong>{resultado.yaExisten}</strong> ya existían (no se tocan) ·{' '}
                            <strong>{resultado.sinCuil}</strong> sin CUIL válido ·{' '}
                            <strong>{resultado.duplicadosEnArchivo}</strong> CUIL repetidos en el archivo.
                            <div style={{ marginTop: 4, fontSize: '0.85rem' }}>
                                Turnos ANSES: {resultado.simulacion ? 'se crearían' : 'se crearon'}{' '}
                                <strong>{resultado.turnosACrear}</strong> (futuros) ·{' '}
                                <strong>{resultado.turnosActualizados}</strong> existentes corregidos (tipo/observación) ·{' '}
                                <strong>{resultado.turnosPasados}</strong> pasados omitidos ·{' '}
                                <strong>{resultado.turnosSinFechaHora}</strong> sin fecha/hora.
                            </div>
                        </div>

                        {resultado.muestra?.length > 0 && (
                            <div style={{ overflowX: 'auto' }}>
                                <div style={{ fontSize: '0.82rem', color: '#64748b', marginBottom: 6 }}>
                                    Muestra ({resultado.muestra.length}) — revisá el corte apellido/nombre y el género deducido del CUIL:
                                </div>
                                <table className="table table-sm table-bordered" style={{ fontSize: '0.8rem' }}>
                                    <thead style={{ background: '#f1f5f9' }}>
                                        <tr>
                                            <th>CUIL</th><th>Apellido</th><th>Nombre</th><th>Teléfono</th>
                                            <th>Género</th><th>Referencia</th><th>Claves</th><th>Cliente</th><th>Turno</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {resultado.muestra.map((f, i) => (
                                            <tr key={i} style={{ background: f.estado === 'YA_EXISTE' ? '#fef9c3' : 'transparent' }}>
                                                <td>{f.cuil}</td>
                                                <td>{f.apellido}</td>
                                                <td>{f.nombre}</td>
                                                <td>{f.telefono || '—'}</td>
                                                <td>{f.genero ? (f.genero === 'MASCULINO' ? 'M' : 'F') : '?'}</td>
                                                <td>{f.referencia || '—'}</td>
                                                <td>{f.claves || '—'}</td>
                                                <td>{f.estado === 'YA_EXISTE' ? 'ya existe' : 'nuevo'}</td>
                                                <td>{f.turno}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {resultado.advertencias?.length > 0 && (
                            <details style={{ marginTop: 8 }}>
                                <summary style={{ cursor: 'pointer', color: '#b45309', fontSize: '0.85rem', fontWeight: 600 }}>
                                    {resultado.advertencias.length} advertencia(s)
                                </summary>
                                <ul style={{ fontSize: '0.82rem', color: '#78716c', marginTop: 6 }}>
                                    {resultado.advertencias.map((a, i) => <li key={i}>{a}</li>)}
                                </ul>
                            </details>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
